import { getRecoil } from 'recoil-nexus';

import { userState } from 'components/state';
import { currentPractice } from 'components/practice/practiceState';

// Owner without a hook, so the effect can read it at atom-init time.
// try/catch: RecoilNexus may have no snapshot on the very first atom init.
const readAtom = (atom, pick) => {
  try {
    return pick(getRecoil(atom)) ?? null;
  } catch {
    return null;
  }
};

const readPracticeId = () => readAtom(currentPractice, (p) => p?.id);
// userState defaults user_id to '' pre-auth — treat that as unresolved, not a mismatch.
const readOwnerId = () => readAtom(userState, (u) => u?.user_id || null);

/**
 * Recoil atom effect mirroring an atom into sessionStorage so it survives a reload.
 * sessionStorage (not localStorage) is deliberate: tab-scoped and cleared on close,
 * so state doesn't leak into a later session on a shared machine.
 *
 * The payload is stamped with the practice id AND the authenticated user id, and restored
 * only on a match: sessionStorage outlives a logout in the same tab, so without the owner
 * tag the next user to sign in on a shared workstation would inherit the previous user's
 * state. A null current id (not resolved yet at boot) is treated as unknown.
 *
 * `sanitize(value)` returns the JSON-serializable slice to persist; applied on both
 * write and restore, so keep it idempotent — never emit refs, functions, or transient fields.
 *
 * @param {{ storageKey: string, sanitize: (value: any) => any }} config
 */
export const sessionPersistEffect =
  ({ storageKey, sanitize }) =>
  ({ setSelf, onSet }) => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        const practiceId = readPracticeId();
        const ownerId = readOwnerId();

        const practiceMatches =
          practiceId == null || saved?.practiceId == null || saved.practiceId === practiceId;
        // A stored blob with no owner predates this tag — drop it rather than hand it
        // to whoever is signed in now.
        const ownerMatches = ownerId == null || saved?.ownerId === ownerId;

        if (practiceMatches && ownerMatches) {
          const restored = sanitize(saved?.value);
          if (restored !== undefined) setSelf(restored);
        } else {
          sessionStorage.removeItem(storageKey);
        }
      }
    } catch {
      // Corrupt or unavailable storage — fall back to the atom's default.
    }

    onSet((newValue, _oldValue, isReset) => {
      try {
        if (isReset) {
          sessionStorage.removeItem(storageKey);
          return;
        }
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            practiceId: readPracticeId(),
            ownerId: readOwnerId(),
            value: sanitize(newValue)
          })
        );
      } catch {
        // Storage full / unavailable — persistence is best-effort, never fatal.
      }
    });
  };
